import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { initializeApp, deleteApp } from 'firebase/app';
import { getAuth, createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { sendWelcomeProvider } from '../services/emailService';
import { supabase } from '../supabase';
import './AddWorkerPage.css';

const secondaryFirebaseConfig = {
    apiKey: "AIzaSyBleAnPBGzpc4yH9fChB2Nmh-b1A-sxT8Y",
    authDomain: "skillhive-6600.firebaseapp.com",
    projectId: "skillhive-6600",
    storageBucket: "skillhive-6600.firebasestorage.app",
    messagingSenderId: "535174388656",
    appId: "1:535174388656:web:8ef433bad78f6792311475"
};

const AddWorkerPage = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        tempPassword: '',
        phone: '',
        service: '',
        experience: '',
        visitFee: '',
        hourlyRate: '',
        location: '',
        bio: '',
        skills: '',
        isActive: true
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [emailStatus, setEmailStatus] = useState('');

    const services = [
        'Electrician',
        'Plumber',
        'Carpenter',
        'AC Repair',
        'Painter',
        'Cleaning',
        'Pest Control',
        'Appliance Repair'
    ];

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setEmailStatus('');

        // Validation
        if (!formData.name || !formData.email || !formData.phone || !formData.service || !formData.tempPassword) {
            setError('Please fill in all required fields (including temporary password)');
            return;
        }

        if (formData.tempPassword.length < 6) {
            setError('Temporary password must be at least 6 characters');
            return;
        }

        let secondaryApp = null;
        try {
            setLoading(true);

            // Create a secondary Firebase app so admin doesn't get signed out
            secondaryApp = initializeApp(secondaryFirebaseConfig, 'workerCreation');
            const secondaryAuth = getAuth(secondaryApp);

            // Create Firebase Auth account for the worker
            const userCredential = await createUserWithEmailAndPassword(
                secondaryAuth,
                formData.email,
                formData.tempPassword
            );
            const workerUid = userCredential.user.uid;

            // Set display name
            await updateProfile(userCredential.user, { displayName: formData.name });

            // Build worker data (exclude tempPassword from stored data)
            const { tempPassword, ...formWithoutPassword } = formData;
            const workerData = {
                ...formWithoutPassword,
                uid: workerUid,
                experience: parseInt(formData.experience) || 0,
                visitFee: parseFloat(formData.visitFee) || 0,
                hourlyRate: parseFloat(formData.hourlyRate) || 0,
                rating: 0,
                completedJobs: 0,
                isVerified: true,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            };

            // Write to Firestore serviceProviders collection with the auth UID
            await setDoc(doc(db, 'serviceProviders', workerUid), workerData);

            // Trigger onboarding email for the newly created worker.
            let emailFailedReason = '';
            try {
                await sendWelcomeProvider(formData.email, {
                    providerName: formData.name,
                    name: formData.name,
                    loginEmail: formData.email,
                    tempPassword: formData.tempPassword,
                    serviceCategory: formData.service,
                    experience: formData.experience || '0',
                    loginUrl: 'https://serviceprovider.skillhives.co.in/login',
                    accountCreatedByAdmin: true,
                });
                setEmailStatus('Welcome email sent successfully.');
            } catch (emailError) {
                console.error('Welcome provider email failed:', emailError);
                const message = emailError?.message || emailError?.context?.json?.error || 'Unknown email error';
                emailFailedReason = message;
                setEmailStatus(`Worker created, but welcome email failed: ${message}`);
            }

            // Clean up secondary app
            await deleteApp(secondaryApp);
            secondaryApp = null;

            const successMessage = `Worker added successfully!\n\nLogin credentials:\nEmail: ${formData.email}\nTemp Password: ${formData.tempPassword}`;
            if (emailFailedReason) {
                alert(`${successMessage}\n\nEmail status: Failed (${emailFailedReason})`);
            } else {
                alert(`${successMessage}\n\nEmail status: Sent`);
            }
            navigate('/workers');
        } catch (error) {
            console.error('Error adding worker:', error);
            if (error.code === 'auth/email-already-in-use') {
                // The worker's Firebase Auth account still exists (e.g. profile was deleted but
                // Auth account was not). Try to recover automatically via the admin edge function.
                try {
                    // 1. Look up the existing UID for this email
                    const { data: lookupData, error: lookupErr } = await supabase.functions.invoke('firebase-admin', {
                        body: { action: 'getUserByEmail', email: formData.email },
                    });
                    if (lookupErr) throw new Error(lookupErr.message || JSON.stringify(lookupErr));
                    const existingUid = lookupData?.uid;
                    if (!existingUid) throw new Error('Could not retrieve existing UID.');

                    // 2. Reset their password and display name to what the admin just entered
                    const { error: updateErr } = await supabase.functions.invoke('firebase-admin', {
                        body: { action: 'updateUser', uid: existingUid, password: formData.tempPassword, displayName: formData.name },
                    });
                    if (updateErr) throw new Error(updateErr.message || JSON.stringify(updateErr));

                    // 3. Re-create the Firestore record with the existing UID
                    const { tempPassword: _pw, ...formWithoutPassword } = formData;
                    const workerData = {
                        ...formWithoutPassword,
                        uid: existingUid,
                        experience: parseInt(formData.experience) || 0,
                        visitFee: parseFloat(formData.visitFee) || 0,
                        hourlyRate: parseFloat(formData.hourlyRate) || 0,
                        rating: 0,
                        completedJobs: 0,
                        isVerified: true,
                        createdAt: new Date().toISOString(),
                        updatedAt: new Date().toISOString(),
                    };
                    await setDoc(doc(db, 'serviceProviders', existingUid), workerData);

                    // 4. Send welcome email (best-effort)
                    try {
                        await sendWelcomeProvider(formData.email, {
                            providerName: formData.name,
                            name: formData.name,
                            loginEmail: formData.email,
                            tempPassword: formData.tempPassword,
                            serviceCategory: formData.service,
                            experience: formData.experience || '0',
                            loginUrl: 'https://serviceprovider.skillhives.co.in/login',
                            accountCreatedByAdmin: true,
                        });
                        setEmailStatus('Welcome email sent successfully.');
                    } catch (emailErr) {
                        setEmailStatus('Worker re-added, but welcome email failed.');
                    }

                    alert(`Worker re-added successfully!\n\nLogin credentials:\nEmail: ${formData.email}\nTemp Password: ${formData.tempPassword}`);
                    navigate('/workers');
                    return;
                } catch (recoveryError) {
                    console.error('Auto-recovery failed:', recoveryError);
                    setError(
                        'This email already exists in Firebase Authentication and auto-recovery failed. ' +
                        'Go to Firebase Console → Authentication and manually delete the user, then try again. ' +
                        `(Reason: ${recoveryError.message})`
                    );
                }
            } else {
                setError('Failed to add worker: ' + error.message);
            }
        } finally {
            if (secondaryApp) {
                try { await deleteApp(secondaryApp); } catch (e) { }
            }
            setLoading(false);
        }
    };

    return (
        <div className="add-worker-page">
            <div className="container">
                <div className="form-container">
                    <div className="form-header">
                        <h1>Add New Worker</h1>
                        <p>Register a new service professional</p>
                    </div>

                    {error && <div className="error-message">{error}</div>}
                    {emailStatus && <div className="error-message" style={{ background: '#f8fafc', color: '#0f172a', border: '1px solid #cbd5e1' }}>{emailStatus}</div>}

                    <form onSubmit={handleSubmit} className="worker-form">
                        <div className="form-section">
                            <h3>Personal Information</h3>
                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="name">Full Name *</label>
                                    <input
                                        type="text"
                                        id="name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        placeholder="John Doe"
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="email">Email *</label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        placeholder="john@example.com"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="tempPassword">Temporary Password *</label>
                                    <input
                                        type="text"
                                        id="tempPassword"
                                        name="tempPassword"
                                        value={formData.tempPassword}
                                        onChange={handleChange}
                                        placeholder="Min 6 characters"
                                        required
                                        minLength="6"
                                    />
                                    <small style={{ color: '#666', marginTop: '4px' }}>Worker will use this to log in for the first time</small>
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="phone">Phone Number *</label>
                                    <input
                                        type="tel"
                                        id="phone"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        placeholder="+91 1234567890"
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="location">Location</label>
                                    <input
                                        type="text"
                                        id="location"
                                        name="location"
                                        value={formData.location}
                                        onChange={handleChange}
                                        placeholder="City, State"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="form-section">
                            <h3>Professional Details</h3>
                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="service">Service Type *</label>
                                    <select
                                        id="service"
                                        name="service"
                                        value={formData.service}
                                        onChange={handleChange}
                                        required
                                    >
                                        <option value="">Select a service</option>
                                        {services.map(service => (
                                            <option key={service} value={service}>{service}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="experience">Years of Experience</label>
                                    <input
                                        type="number"
                                        id="experience"
                                        name="experience"
                                        value={formData.experience}
                                        onChange={handleChange}
                                        placeholder="5"
                                        min="0"
                                    />
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="visitFee">Visit Fee (₹) *</label>
                                    <input
                                        type="number"
                                        id="visitFee"
                                        name="visitFee"
                                        value={formData.visitFee}
                                        onChange={handleChange}
                                        placeholder="300"
                                        min="0"
                                        required
                                    />
                                    <small style={{ color: '#666', marginTop: '4px' }}>Includes 1st hour of work</small>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="hourlyRate">Hourly Rate (₹) *</label>
                                    <input
                                        type="number"
                                        id="hourlyRate"
                                        name="hourlyRate"
                                        value={formData.hourlyRate}
                                        onChange={handleChange}
                                        placeholder="200"
                                        min="0"
                                        required
                                    />
                                    <small style={{ color: '#666', marginTop: '4px' }}>Per hour after 1st hour (1.5× after 8 hrs/day)</small>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="skills">Skills (comma-separated)</label>
                                    <input
                                        type="text"
                                        id="skills"
                                        name="skills"
                                        value={formData.skills}
                                        onChange={handleChange}
                                        placeholder="Wiring, Installation, Troubleshooting"
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="bio">Bio / Description</label>
                                <textarea
                                    id="bio"
                                    name="bio"
                                    value={formData.bio}
                                    onChange={handleChange}
                                    placeholder="Brief description about the professional..."
                                    rows="4"
                                />
                            </div>

                            <div className="form-group checkbox-group">
                                <label className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        name="isActive"
                                        checked={formData.isActive}
                                        onChange={handleChange}
                                    />
                                    <span>Set as Active (available for work)</span>
                                </label>
                            </div>
                        </div>

                        <div className="form-actions">
                            <button
                                type="button"
                                onClick={() => navigate('/workers')}
                                className="btn btn-outline"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={loading}
                            >
                                {loading ? 'Adding Worker...' : 'Add Worker'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default AddWorkerPage;
